// web-commands.ts — the PER-APP half of the web shape: maps workout_tracker's Tauri data
// commands (normally served by ipc.rs/db.rs over native SQLite) to the shared browser WebStore.
// Plain CRUD (workouts + nested sets) is a few lines each. The derived commands (list_prs,
// trend_workouts, trend_signals) are the app's "paid draw" computed in db.rs — on web they
// return a minimal valid shell for now; a faithful web build reimplements that logic in JS
// (the known per-app wrinkle, see MACOS/web docs).

import type { CommandHandler, Row, WebStore } from "./web-runtime";
import { browserSearch, browserFetch } from "./web-search-browser";
import { detectPr, listPrsWeb, trendWorkoutsWeb, trendSignalsWeb, exerciseHistoryWeb, type WebSet, type WebWorkout } from "./signals-web";

const WORKOUTS = "workouts";
const SETS = "sets";
const ROUTINES = "routines";
const TRAINING_PROFILE = "training_profile";
const PROGRAMS = "programs";
const PROGRAM_SESSIONS = "program_sessions";

function uuid(): string {
  // crypto.randomUUID is available in every modern browser.
  return typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.round(Math.random() * 1e9)}`;
}
function nowIso(): string {
  return new Date().toISOString();
}
function today(): string {
  const d = new Date();
  return `${d.getFullYear().toString().padStart(4, "0")}-${(d.getMonth() + 1)
    .toString()
    .padStart(2, "0")}-${d.getDate().toString().padStart(2, "0")}`;
}
function cleanNotes(v: unknown): string | null {
  if (v == null) return null;
  const t = String(v).trim();
  return t === "" ? null : t;
}

export function makeWebCommands(db: WebStore): Record<string, CommandHandler> {
  // Compute (total_sets, total_volume) rollups for a workout from its sets (mirrors db.rs::rollups).
  const rollups = (workoutId: string): { total_sets: number; total_volume: number } => {
    const sets = db.all(SETS).filter((s) => (s as { workout_id?: string }).workout_id === workoutId);
    const total_volume = sets.reduce(
      (n, s) => n + Number((s as { weight?: number }).weight ?? 0) * Number((s as { reps?: number }).reps ?? 0),
      0,
    );
    return { total_sets: sets.length, total_volume };
  };
  const setsForWorkout = (workoutId: string): Row[] =>
    db
      .all(SETS)
      .filter((s) => (s as { workout_id?: string }).workout_id === workoutId)
      .sort((a, b) => {
        const ai = Number((a as { set_index?: number }).set_index ?? 0);
        const bi = Number((b as { set_index?: number }).set_index ?? 0);
        if (ai !== bi) return ai - bi;
        return String((a as { created_at?: string }).created_at ?? "").localeCompare(
          String((b as { created_at?: string }).created_at ?? ""),
        );
      });

  return {

    // ── WEB-SURFACE RETRIEVAL (Pattern 7, web half) ─────────────────────────────
    // On native, `web_search`/`web_fetch` are Rust commands browsing on the DEVICE. In a browser
    // there is no Rust, and without these the chat's `maybeWebContext` silently returns nothing —
    // the customer gets an ungrounded answer where the phone would have cited sources.
    // Here the BROWSER does it, on the customer's own IP: Tier 1 sources it may read directly
    // (no middleman), then a keyless reader for the open web. Oriora runs no search.
    web_search: async (args: Record<string, unknown>) =>
      await browserSearch(String(args.query ?? ""), typeof args.limit === "number" ? args.limit : 5),
    web_fetch: async (args: Record<string, unknown>) =>
      await browserFetch(String(args.url ?? ""), typeof args.maxChars === "number" ? args.maxChars : 6000),

    // ── Workouts (plain CRUD; rollups computed on read like the native side) ──
    list_workouts: ({ limit }) => {
      const rows = db.all(WORKOUTS).sort((a, b) => {
        const ad = String((a as { performed_on?: string }).performed_on ?? "");
        const bd = String((b as { performed_on?: string }).performed_on ?? "");
        if (ad !== bd) return bd.localeCompare(ad); // performed_on DESC
        return String((b as { created_at?: string }).created_at ?? "").localeCompare(
          String((a as { created_at?: string }).created_at ?? ""),
        ); // created_at DESC
      });
      const lim = typeof limit === "number" ? limit : 50000;
      return rows.slice(0, lim).map((w) => ({ ...w, ...rollups(String((w as Row).id)) }));
    },
    get_workout: ({ id }) => {
      const w = db.get(WORKOUTS, String(id));
      if (!w) return null;
      return { workout: { ...w, ...rollups(String(id)) }, sets: setsForWorkout(String(id)) };
    },
    create_workout: ({ input }) => {
      const i = (input ?? {}) as Record<string, unknown>;
      const name = String(i.name ?? "").trim() || "Workout";
      const row = {
        id: uuid(),
        name,
        performed_on: String(i.performed_on ?? today()),
        duration_minutes: Number(i.duration_minutes ?? 0),
        notes: cleanNotes(i.notes),
        created_at: nowIso(),
        photo: "",
        finished_at: null,
      };
      db.insert(WORKOUTS, row as { id: string } & Record<string, unknown>);
      return { ...row, total_sets: 0, total_volume: 0 };
    },
    // Attach a photo to a workout — filed ON-DEVICE, never sent to the model (web mirror of the native command).
    set_workout_photo: ({ id, photo }) => {
      db.update(WORKOUTS, String(id), { photo: String(photo ?? "") });
      return null;
    },
    delete_workout: ({ id }) => {
      db.all(SETS)
        .filter((s) => (s as { workout_id?: string }).workout_id === String(id))
        .forEach((s) => db.remove(SETS, String((s as Row).id)));
      db.remove(WORKOUTS, String(id));
      return null;
    },

    // ── Routines (reusable templates; plain CRUD, exercises stored as an array) ──
    list_routines: () =>
      db.all(ROUTINES).sort((a, b) =>
        String((b as { created_at?: string }).created_at ?? "").localeCompare(
          String((a as { created_at?: string }).created_at ?? ""),
        ),
      ),
    create_routine: ({ input }) => {
      const i = (input ?? {}) as Record<string, unknown>;
      const exercises = Array.isArray(i.exercises)
        ? (i.exercises as Array<{ slug?: unknown; name?: unknown }>).map((e) => ({
            slug: String(e?.slug ?? ""),
            name: String(e?.name ?? ""),
          }))
        : [];
      const row = {
        id: uuid(),
        name: String(i.name ?? "").trim() || "Routine",
        exercises,
        created_at: nowIso(),
      };
      return db.insert(ROUTINES, row as { id: string } & Record<string, unknown>);
    },
    delete_routine: ({ id }) => {
      db.remove(ROUTINES, String(id));
      return null;
    },

    // ── Sets (nested rows → their own table, plain CRUD). PR flags are DERIVED and shelled. ──
    add_set: ({ input }) => {
      const i = (input ?? {}) as Record<string, unknown>;
      const workoutId = String(i.workout_id ?? "");
      // Next set_index within this workout (mirrors MAX(set_index)+1).
      const setIndex =
        db
          .all(SETS)
          .filter((s) => (s as { workout_id?: string }).workout_id === workoutId)
          .reduce((mx, s) => Math.max(mx, Number((s as { set_index?: number }).set_index ?? -1)), -1) + 1;
      const row = {
        id: uuid(),
        workout_id: workoutId,
        exercise_slug: String(i.exercise_slug ?? ""),
        exercise_name: String(i.exercise_name ?? "").trim(),
        set_index: setIndex,
        weight: Number(i.weight ?? 0),
        reps: Number(i.reps ?? 0),
        rpe: Number(i.rpe ?? 0),
        // PR detection against prior history — db.rs::add_set ported (zero-stub sweep 2026-08-20)
        ...detectPr(
          db.all(SETS)
            .filter((s) => (s as { exercise_slug?: string }).exercise_slug === String(i.exercise_slug ?? ""))
            .map((s) => ({ weight: Number((s as { weight?: number }).weight ?? 0), reps: Number((s as { reps?: number }).reps ?? 0), workout_id: String((s as { workout_id?: string }).workout_id ?? "") })),
          { workout_id: workoutId, weight: Number(i.weight ?? 0), reps: Number(i.reps ?? 0) },
        ),
        notes: cleanNotes(i.notes),
        created_at: nowIso(),
      };
      return db.insert(SETS, row as { id: string } & Record<string, unknown>);
    },
    delete_set: ({ id }) => {
      db.remove(SETS, String(id));
      return null;
    },

    // ── In-session (resumable) state + previous-session anchor (mirror db.rs) ──
    // A web workout is OPEN only if it EXPLICITLY carries finished_at === null. Pre-existing
    // localStorage workouts lack the key entirely (undefined) → treated as completed records, the
    // same "existing history is finished" result the native backfill produces. No web migration needed.
    active_workout_id: () => {
      const open = db.all(WORKOUTS)
        .filter((w) => Object.prototype.hasOwnProperty.call(w, "finished_at") && (w as { finished_at?: string | null }).finished_at == null)
        .sort((a, b) => String((b as { created_at?: string }).created_at ?? "").localeCompare(String((a as { created_at?: string }).created_at ?? "")));
      return open[0] ? String((open[0] as Row).id) : null;
    },
    finish_workout: ({ id }) => {
      db.update(WORKOUTS, String(id), { finished_at: nowIso() });
      return null;
    },
    previous_exercise_sets: ({ slug, exclude_workout_id }) => {
      const sl = String(slug ?? "");
      const excl = String(exclude_workout_id ?? "");
      const workoutById = new Map(db.all(WORKOUTS).map((w) => [String((w as Row).id), w]));
      // Most recent OTHER workout (performed_on DESC, created_at DESC) that contains this exercise.
      const candidates = db.all(SETS)
        .filter((s) => (s as { exercise_slug?: string }).exercise_slug === sl && String((s as { workout_id?: string }).workout_id ?? "") !== excl)
        .map((s) => String((s as { workout_id?: string }).workout_id ?? ""));
      const uniqWids = [...new Set(candidates)];
      uniqWids.sort((a, b) => {
        const wa = workoutById.get(a) as { performed_on?: string; created_at?: string } | undefined;
        const wb = workoutById.get(b) as { performed_on?: string; created_at?: string } | undefined;
        const pa = String(wa?.performed_on ?? ""), pb = String(wb?.performed_on ?? "");
        if (pa !== pb) return pb.localeCompare(pa);
        return String(wb?.created_at ?? "").localeCompare(String(wa?.created_at ?? ""));
      });
      const wid = uniqWids[0];
      if (!wid) return [];
      return db.all(SETS)
        .filter((s) => String((s as { workout_id?: string }).workout_id ?? "") === wid && (s as { exercise_slug?: string }).exercise_slug === sl)
        .map((s) => ({ weight: Number((s as { weight?: number }).weight ?? 0), reps: Number((s as { reps?: number }).reps ?? 0), rpe: Number((s as { rpe?: number }).rpe ?? 0), set_index: Number((s as { set_index?: number }).set_index ?? 0) }))
        .sort((a, b) => a.set_index - b.set_index);
    },

    // ── Derived: all-time PRs per exercise (native reduces every set in db.rs). ──
    list_prs: () => listPrsWeb(db.all(SETS) as unknown as WebSet[], db.all(WORKOUTS) as unknown as WebWorkout[]),
    exercise_history: ({ slug }) => exerciseHistoryWeb(db.all(SETS) as unknown as WebSet[], db.all(WORKOUTS) as unknown as WebWorkout[], String(slug ?? "")),

    // ── Derived: last-N-days workouts shaped for the AI brain. ──
    trend_workouts: ({ days }) =>
      trendWorkoutsWeb(db.all(SETS) as unknown as WebSet[], db.all(WORKOUTS) as unknown as WebWorkout[], Number(days ?? 14), new Date().toISOString().slice(0, 10)),

    // ── Derived: training signals (aggregates). Shell matches TrendSignals in store.ts so the
    //    UI never crashes; the AI recap is degraded until the signal logic is ported to JS. ──
    trend_signals: ({ days }) =>
      trendSignalsWeb(db.all(SETS) as unknown as WebSet[], db.all(WORKOUTS) as unknown as WebWorkout[], Number(days ?? 14), new Date().toISOString().slice(0, 10)),

    // ── Training profile + AI-drafted programs (PROGRAM-DESIGN.md). Plain CRUD, unlike list_prs/
    //    trend_signals above — these are real on web, same as workouts/routines. Only the
    //    adaptive-retarget ARITHMETIC in complete_program_session is native-only derived logic
    //    (mirrors db.rs::complete_program_session); that one piece degrades honestly below. ──
    get_training_profile: () => db.get(TRAINING_PROFILE, "default"),
    set_training_profile: ({ goal, days_per_week, avoid }) => {
      const row = { id: "default", goal: String(goal ?? ""), days_per_week: Number(days_per_week ?? 0), avoid: String(avoid ?? ""), updated_at: nowIso() };
      if (db.get(TRAINING_PROFILE, "default")) db.update(TRAINING_PROFILE, "default", row);
      else db.insert(TRAINING_PROFILE, row as { id: string } & Record<string, unknown>);
      return row;
    },
    active_program: () => {
      const rows = db.all(PROGRAMS).filter((p) => (p as { status?: string }).status === "active");
      if (!rows.length) return null;
      return rows.sort((a, b) => String((b as { created_at?: string }).created_at ?? "").localeCompare(String((a as { created_at?: string }).created_at ?? "")))[0];
    },
    list_program_sessions: ({ program_id }) =>
      db.all(PROGRAM_SESSIONS)
        .filter((s) => (s as { program_id?: string }).program_id === String(program_id))
        .sort((a, b) => {
          const aw = Number((a as { week_index?: number }).week_index ?? 0), bw = Number((b as { week_index?: number }).week_index ?? 0);
          if (aw !== bw) return aw - bw;
          return Number((a as { day_index?: number }).day_index ?? 0) - Number((b as { day_index?: number }).day_index ?? 0);
        }),
    next_program_session: () => {
      const active = db.all(PROGRAMS).filter((p) => (p as { status?: string }).status === "active")
        .sort((a, b) => String((b as { created_at?: string }).created_at ?? "").localeCompare(String((a as { created_at?: string }).created_at ?? "")))[0];
      if (!active) return null;
      const pending = db.all(PROGRAM_SESSIONS)
        .filter((s) => (s as { program_id?: string }).program_id === (active as Row).id && (s as { completed_workout_id?: string | null }).completed_workout_id == null)
        .sort((a, b) => {
          const aw = Number((a as { week_index?: number }).week_index ?? 0), bw = Number((b as { week_index?: number }).week_index ?? 0);
          if (aw !== bw) return aw - bw;
          return Number((a as { day_index?: number }).day_index ?? 0) - Number((b as { day_index?: number }).day_index ?? 0);
        });
      return pending[0] ?? null;
    },
    create_program: ({ input }) => {
      const i = (input ?? {}) as { name?: unknown; goal?: unknown; weeks?: Array<{ week_index?: number; sessions?: Array<{ day_index?: number; label?: unknown; exercises?: unknown }> }> };
      db.all(PROGRAMS).filter((p) => (p as { status?: string }).status === "active")
        .forEach((p) => db.update(PROGRAMS, String((p as Row).id), { status: "abandoned" }));
      const id = uuid();
      const weeks = Array.isArray(i.weeks) ? i.weeks : [];
      const program = { id, name: String(i.name ?? "Program"), goal: String(i.goal ?? ""), weeks_count: weeks.length, status: "active", created_at: nowIso() };
      db.insert(PROGRAMS, program as { id: string } & Record<string, unknown>);
      for (const week of weeks) {
        for (const session of week.sessions ?? []) {
          db.insert(PROGRAM_SESSIONS, {
            id: uuid(), program_id: id, week_index: Number(week.week_index ?? 0), day_index: Number(session.day_index ?? 0),
            label: String(session.label ?? ""), exercises: session.exercises ?? [], completed_workout_id: null,
          } as { id: string } & Record<string, unknown>);
        }
      }
      return program;
    },
    abandon_active_program: () => {
      db.all(PROGRAMS).filter((p) => (p as { status?: string }).status === "active")
        .forEach((p) => db.update(PROGRAMS, String((p as Row).id), { status: "abandoned" }));
      return null;
    },
    complete_program_session: ({ session_id, workout_id }) => {
      db.update(PROGRAM_SESSIONS, String(session_id), { completed_workout_id: String(workout_id) });
      // TODO: port the adaptive re-target arithmetic (db.rs::complete_program_session) to JS —
      // native-only derived logic, same honest-degradation shape as list_prs/trend_signals above.
      // The session still marks done correctly; future targets just won't auto-adjust on web yet.
      return null;
    },
  };
}
